/**
 * useSageVoicePreference — small hook + in-memory cache for the current
 * user's preferred Kokoro voice. Shared by SageTTSButton (per-message play)
 * and the upcoming conversation mode auto-playback so they speak in the
 * user's voice of choice.
 */
import { authedFetch } from '@/lib/api';
import { useEffect, useState } from 'react'

const DEFAULT_VOICE = 'af_heart'
const CACHE_KEY = 'sage_voice_pref_v1'

let inflight: Promise<string> | null = null
let cached: string | null = null
const listeners = new Set<(v: string) => void>()

function notify(v: string) {
  cached = v
  localStorage.setItem(CACHE_KEY, v)
  listeners.forEach(fn => fn(v))
}

async function fetchPref(): Promise<string> {
  try {
    const res = await authedFetch('/api/v1/sage/voice-preference', {}, { redirectOnAuthFailure: false })
    if (!res.ok) return DEFAULT_VOICE
    const data = await res.json() as { voice?: string }
    return data.voice || DEFAULT_VOICE
  } catch {
    return DEFAULT_VOICE
  }
}

export function useSageVoicePreference(): [string, (v: string) => Promise<void>] {
  const [voice, setVoice] = useState<string>(() => cached || localStorage.getItem(CACHE_KEY) || DEFAULT_VOICE)

  useEffect(() => {
    const listener = (v: string) => setVoice(v)
    listeners.add(listener)
    if (cached === null) {
      if (!inflight) inflight = fetchPref().then(v => { inflight = null; notify(v); return v })
      inflight.then(v => setVoice(v))
    }
    return () => { listeners.delete(listener) }
  }, [])

  const update = async (v: string) => {
    notify(v) // optimistic
    try {
      await authedFetch('/api/v1/sage/voice-preference', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ voice: v }),
      }, { redirectOnAuthFailure: false })
    } catch {
      // Save failed; leave optimistic value — next page load will re-fetch.
    }
  }

  return [voice, update]
}
